import React from 'react';
import { Head, Section } from '@/components';
import { motion, Variants } from 'framer-motion';
import { Check, ArrowRight, Clock, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * ThankYou page component.
 * Premium, polished post-submission experience.
 */
const ThankYou: React.FC = () => {
    // Animation variants
    const containerVariants: Variants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2
            }
        }
    };

    const itemVariants: Variants = {
        hidden: { opacity: 0, y: 20 },
        visible: { 
            opacity: 1, 
            y: 0, 
            transition: { 
                duration: 0.6, 
                ease: [0.22, 1, 0.36, 1] as const 
            } 
        }
    };

  return (
    <>
      <Head 
        title="Message Received | Kivosystems"
        description="Thank you for contacting Kivosystems. We have received your message and will be in touch shortly."
        canonicalUrl="https://www.kivosystems.com/thank-you"
        url="https://www.kivosystems.com/thank-you"
        image="/logo.svg"
        noIndex={true}
      />

      <Section className="bg-obsidian min-h-screen pt-24 pb-12 relative overflow-hidden flex items-center justify-center">
        {/* Cinematic Background */}
        <div className="absolute inset-0 opacity-30 pointer-events-none">
            <div className="absolute top-[-10%] right-[-5%] w-[800px] h-[800px] bg-sapphire-900/30 rounded-full blur-[140px] animate-pulse-slow" />
            <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-indigo-900/20 rounded-full blur-[140px]" />
            <div className="absolute top-[20%] left-[20%] w-[300px] h-[300px] bg-sky-500/10 rounded-full blur-[100px]" />
        </div>

        {/* Noise Texture Overlay */}
        <div className="absolute inset-0 opacity-[0.03] bg-[url('/noise.png')] pointer-events-none mix-blend-overlay" />

        <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="w-full max-w-lg mx-auto relative z-10 px-4"
        >
            {/* Main Success Card */}
            <motion.div variants={itemVariants} className="glass border border-white/10 p-8 md:p-12 rounded-3xl shadow-2xl relative overflow-hidden group">
                <div className="absolute inset-0 bg-linear-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
                
                <div className="relative z-10 text-center">
                    <motion.div 
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
                        className="w-20 h-20 bg-linear-to-br from-sapphire-500/20 to-indigo-500/20 rounded-full flex items-center justify-center mx-auto mb-8 ring-1 ring-white/10 shadow-[0_0_30px_rgba(59,130,246,0.3)]"
                    >
                        <Check className="w-10 h-10 text-sapphire-400 drop-shadow-[0_0_10px_rgba(59,130,246,0.5)]" />
                    </motion.div>

                    <h1 className="text-4xl md:text-5xl font-display font-medium text-white mb-4 tracking-tight">
                        Received.
                    </h1>
                    <p className="text-gray-400 text-lg mb-10 leading-relaxed font-light">
                        Thank you for initiating the conversation. Your request has been securely logged with our engineering team.
                    </p>

                    {/* Next Steps Timeline */}
                    <div className="text-left bg-black/20 rounded-2xl p-6 mb-10 border border-white/5 mx-auto">
                        <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-6">What Happens Next</p>
                        <div className="space-y-6">
                            <div className="flex items-start gap-4">
                                <div className="mt-1 p-1.5 bg-sapphire-500/10 rounded-full text-sapphire-400">
                                    <Clock className="w-4 h-4" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-bold text-white">Review (&lt; 24 Hours)</h4>
                                    <p className="text-xs text-gray-500 mt-1">Our technical team reviews your project requirements.</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="mt-1 p-1.5 bg-indigo-500/10 rounded-full text-indigo-400">
                                    <ShieldCheck className="w-4 h-4" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-bold text-white">Discovery Invite</h4>
                                    <p className="text-xs text-gray-500 mt-1">We'll email you to schedule a secure discovery call.</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="mt-1 p-1.5 bg-purple-500/10 rounded-full text-purple-400">
                                    <Sparkles className="w-4 h-4" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-bold text-white">Proposal & Strategy</h4>
                                    <p className="text-xs text-gray-500 mt-1">You receive a tailored roadmap and quote.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Link to="/" className="inline-block w-full">
                        <button 
                            className="w-full bg-white text-black hover:bg-gray-200 px-8 py-4 rounded-xl font-bold transition-all transform hover:-translate-y-0.5 shadow-lg flex items-center justify-center gap-2 group"
                        >
                            Back to Homepage
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </Link>
                </div>
            </motion.div>
            
            <motion.p variants={itemVariants} className="text-center text-xs text-gray-600 mt-8">
                &copy; {new Date().getFullYear()} Kivosystems. Engineering Excellence.
            </motion.p>
        </motion.div>
      </Section>
    </>
  );
};

export default ThankYou;
